import { api } from '@/lib/api/client'
import type { PresenceStatus } from '@/types/presence'
import type { UserAvatar } from '@/types/UserAvatar'

export interface AdminUser {
  db_id: string
  name: string
  username: string
  email: string | null
  role: string
  active: boolean
  permissions: string[]
  sub_roles: string[]
  channel_ids: string[]
  parameters: Record<string, string>
  has_global_user: boolean
  presence_status?: PresenceStatus
  last_seen_at?: number | null
  /** Mesmo campo do directory (`ISystemUser.avatar`) — gestão devolve o dossiê
   *  completo, e o avatar continua parte da identidade. */
  avatar: UserAvatar | null
}

export interface SubRoleShortInfo {
  key: string
  name: string
  minimum_role: string
  active: boolean
}

export interface AdminUserDetail extends AdminUser {
  scopes: Record<string, string[]>
  permissions_excluded: string[]
  scopes_excluded: Record<string, string[]>
  sub_roles_detail: SubRoleShortInfo[]
}

export interface UpdateUserPayload {
  name?: string
  role?: string
  permissions?: string[]
  sub_roles?: string[]
  scopes?: Record<string, string[]>
  permissions_excluded?: string[]
  scopes_excluded?: Record<string, string[]>
  channel_ids?: string[]
  active?: boolean
}

export interface RegisterUserPayload {
  name: string
  email?: string | null
  role: string
  permissions?: string[]
  sub_roles?: string[]
}

export interface RegisterUserResponse {
  id: string
  name: string
  username: string
  password: string
}

export interface PasswordResetResult {
  username: string
  new_password: string
  updated_at: number
}

export async function listUsers(): Promise<AdminUser[]> {
  const { data } = await api.get<AdminUser[]>('/users')
  return data
}

export async function getUser(userId: string): Promise<AdminUserDetail> {
  const { data } = await api.get<AdminUserDetail>(`/users/${userId}`)
  return data
}

export async function registerUser(payload: RegisterUserPayload): Promise<RegisterUserResponse> {
  const { data } = await api.post<RegisterUserResponse>('/auth/register', payload)
  return data
}

export async function updateUser(userId: string, payload: UpdateUserPayload): Promise<{ success: boolean }> {
  const { data } = await api.patch<{ success: boolean }>(`/users/${userId}`, payload)
  return data
}

export async function deactivateUser(userId: string): Promise<{ success: boolean }> {
  const { data } = await api.delete<{ success: boolean }>(`/users/${userId}`)
  return data
}

export async function hardDeleteUser(userId: string): Promise<{ success: boolean }> {
  const { data } = await api.delete<{ success: boolean }>(`/users/${userId}`, { params: { permanent: true } })
  return data
}

export async function resetUserPassword(userId: string): Promise<PasswordResetResult> {
  const { data } = await api.post<PasswordResetResult>(`/users/${userId}/password`)
  return data
}

export async function setUserChannels(userId: string, channelIds: string[]): Promise<void> {
  await api.post(`/users/${userId}/subscribed_channels`, { channels_ids: channelIds })
}

export async function assignSubRole(key: string, userId: string): Promise<{ changed: boolean }> {
  const { data } = await api.post<{ changed: boolean }>(`/sub_roles/${key}/users/${userId}`)
  return data
}

export async function unassignSubRole(key: string, userId: string): Promise<{ changed: boolean }> {
  const { data } = await api.delete<{ changed: boolean }>(`/sub_roles/${key}/users/${userId}`)
  return data
}
